/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { DateRange } from '@app/common/types/date-range';
import { DatepickerRangeOptions } from '@wfm/core';
import { appConstants } from '@app/common/app-constants';
import { UserWorkListParams } from '@app/common/types/user-work-list-params';
import { isEmpty, isUndefined } from '@core/lib/helpers/helpers';
import { Logger } from '@core/lib/services';

const log = new Logger('SearchParams');

export class SearchParams {
  skipProps = [
    'dateFormat',
    'skipProps',
    'orderBy',
    'statsOnTop',
    'viewerPreference',
    'searchCriteriaType',
    'searchTimeStamp',
    'repositoryList'
  ];

  searchCriteriaType: SearchCriteriaType;
  searchTimeStamp: number;
  repositoryList: any[];
  advancedSearch: boolean;
  sort: any;

  // Date Range fields
  dob: DateRange = {
    startTime: undefined,
    endTime: undefined,
    option: DatepickerRangeOptions.customRange
  };
  timeOfExam: DateRange = {
    startTime: undefined,
    endTime: undefined,
    option: DatepickerRangeOptions.lastWeek
  };
  verifiedDateTime: DateRange = {
    startTime: undefined,
    endTime: undefined,
    option: DatepickerRangeOptions.allDates
  };

  // text fields
  worklistName: string;
  patientName: string;
  mrn: string;
  nhi: string;
  accession: string;
  orderNumber: string;
  departmentId: string;
  orderingLocationId: string;
  studyInstanceUID: string;

  worklistId: number;

  //linked properties
  isLinked = false;
  filterJson: string;
  parentWorkListId: number;

  // dropdown fields
  modality: any[];
  bodyParts: any[];
  descriptions: any[];
  specialties: any[];
  departmentIds: any[];
  examStatusIds: any[];
  protocolStatus: any[];
  initialInterpreters: any[]; // initialAuthor
  referringPhysician: any[];
  radiologists: any[]; // responsibleAuthor
  designatedAuthor: any[]; // designatedAuthor
  instituteList: any[];
  currentLocation: any[]; // patientLocation
  orderingLocation: any[];
  reportStatusIds: any[];
  site: any[];
  priority: any[];
  exceptions: any[];
  aiFinding: any[];
  equipment: any[];
  isGlobalWorklist: any;

  // Preference tab fields
  excludeZeroImageExams = true;
  orderBy = true;
  statsOnTop = false;
  viewerPreference = true;

  constructor(searchCriteriaType: SearchCriteriaType) {
    this.searchCriteriaType = searchCriteriaType;
    this.advancedSearch = searchCriteriaType === SearchCriteriaType.ADVANCED_SEARCH;
  }

  isRemoteSearch(): boolean {
    return !!(this.repositoryList && this.repositoryList.length);
  }

  buildRequestParams(searchType: SearchCriteriaType, layoutAttributes: string): any {
    const params = this.getFormattedSearchParams(searchType, true);
    params['advancedSearch'] = this.advancedSearch;
    if (this.sort && !_.isEmpty(this.sort)) {
      params['sort'] = this.sort;
    }
    params['layoutAttributes'] = layoutAttributes;
    return params;
  }

  getSearchFilterJson(searchType: SearchCriteriaType) {
    const params: any = this.getFormattedSearchParams(searchType, false);
    const jsonArr = [];
    const keys = Object.keys(params);
    if (keys) {
      keys.forEach(key => {
        const value = params[key];
        if (value instanceof Array) {
          jsonArr.push(`${key}:${value.join(',')}`);
        } else {
          jsonArr.push(`${key}:${value}`);
        }
      });
    }

    return jsonArr && jsonArr.length && jsonArr.join(';');
  }

  buildUserDefinedWLParams(): UserWorkListParams {
    const params: UserWorkListParams = new UserWorkListParams();
    params.statsOnTop = this.statsOnTop ? 1 : 0;
    params.viewerPreferences = this.viewerPreference ? 1 : 0;

    params.worklistName = this.worklistName;
    params.examDateRangeToken =
      !isUndefined(this.timeOfExam?.option) && this.timeOfExam.option != 0
        ? appConstants.datePickerRangeValues[this.timeOfExam.option]
        : null;
    params.beginExamDateRange = isUndefined(params.examDateRangeToken)
      ? this.timeOfExam.startTime && this.timeOfExam.startTime.startOf('day').format('M/D/YYYY HH:mm:ss A')
      : null;
    params.endExamDateRange = isUndefined(params.examDateRangeToken)
      ? this.timeOfExam.endTime && this.timeOfExam.endTime.endOf('day').format('M/D/YYYY HH:mm:ss A')
      : null;
    params.verifiedDateRangeToken = isUndefined(this.verifiedDateTime?.option)
      ? null
      : appConstants.datePickerRangeValues[this.verifiedDateTime.option];
    params.verifiedBeginDate = isUndefined(params.verifiedDateRangeToken)
      ? this.verifiedDateTime.startTime && this.verifiedDateTime.startTime.startOf('day').format('M/D/YYYY HH:mm A')
      : null;
    params.verifiedEndDate = isUndefined(params.verifiedDateRangeToken)
      ? this.verifiedDateTime.endTime && this.verifiedDateTime.endTime.endOf('day').format('M/D/YYYY HH:mm A')
      : null;
    params.patientBirthBeginDate = this.dob?.startTime
      ? this.dob.startTime.startOf('day').format('M/D/YYYY HH:mm A')
      : null;
    params.patientBirthEndDate = this.dob?.endTime ? this.dob.endTime.endOf('day').format('M/D/YYYY HH:mm A') : null;
    params.patientName = this.patientName;
    params.patientIdentifier = this.getPatientIdentifier(this.mrn, this.nhi);
    params.departmentCkey = this.departmentId;
    params.orderingLocationCkey = this.orderingLocationId;
    params.orderNumber = this.orderNumber;
    params.accession = this.accession;

    params.modalityList = this.getSearchParamValue('modality');
    params.examStatusList = this.getSearchParamValue('examStatusIds');
    params.filterParamsList = null;
    params.procedureDescriptionList = this.getSearchParamValue('descriptions');
    params.orderingLocationList = this.getSearchParamValue('orderingLocation');
    params.protocolStatusList = this.getSearchParamValue('protocolStatus');
    params.bodyPartList = this.getSearchParamValue('bodyParts');
    params.departmentList = this.getSearchParamValue('departmentIds');
    params.examSpecialtyList = this.getSearchParamValue('specialties');
    params.instituteList = this.getSearchParamValue('instituteList');
    params.reportStatusList = this.getSearchParamValue('reportStatusIds');
    params.equipmentList = this.getSearchParamValue('equipment');
    params.siteList = this.getSearchParamValue('site');
    params.exceptions = this.getSearchParamValue('exceptions');
    // params.orderingLocationDtoList = this.getSearchParamValue('');
    // params.assignedToList = this.getSearchParamValue('');
    params.initialInterpreterList = this.getSearchParamValue('initialInterpreters');
    params.radiologistList = this.getSearchParamValue('radiologists');
    params.designatedAuthorList = this.getSearchParamValue('designatedAuthor');
    params.referringPhysician = this.getSearchParamValue('referringPhysician');
    params.examPriorityList = this.getSearchParamValue('priority');
    const patientLocations = this.getSearchParamValue('currentLocation', 'id');
    params.patientLocationList = patientLocations && patientLocations.length ? patientLocations.map(Number) : null;
    params.aifindingList = this.getSearchParamValue('aiFinding');

    params.excludeZeroImageExams = this.excludeZeroImageExams;
    params.orderBy = this.orderBy;

    // linked props
    params.isLinked = this.isLinked;
    params.filterJson = this.filterJson;
    params.parentWorkListId = this.parentWorkListId;

    Object.keys(params).forEach(key => {
      if (params.hasOwnProperty(key) && isUndefined(params[key])) {
        params[key] = null;
      }
    });

    return params;
  }

  getSearchParamValue(key: string, id?: string): any {
    const value = this[key];
    if (value !== undefined) {
      if (typeof value === 'string') {
        return value;
      } else if (value instanceof Array && value.length) {
        const propName = isUndefined(id) ? SearchParamKeys[key] : id;
        const values = value.map(obj => obj[propName]);
        if (values && values.length) {
          return values;
        }
      }
    }
  }

  getPatientIdentifier(mrn, nhi) {
    if (nhi) {
      return 'nhi:' + this.nhi;
    } else if (mrn) {
      return this.buildServiceParamsWithWildcard(this.mrn);
    }
  }

  setPreferenceParams() {
    const selectedPreference = {};
    // Stat on top preference
    if (this.statsOnTop) {
      selectedPreference[appConstants.SORT_BY.Priority] = appConstants.SORT_BY.descendingOrder;
    }
    this.sort = selectedPreference;
  }

  private getFormattedSearchParams(searchType: string, isRequestParams = true): any {
    const params: any = {};
    if (searchType === SearchCriteriaType.STUDY_INSTANCE_UID) {
      params.studyInstanceUID = encodeURIComponent(this.studyInstanceUID);
    } else if (searchType === SearchCriteriaType.ACCESSION) {
      params.accession = encodeURIComponent(this.buildServiceParamsWithWildcard(this.accession));
    } else if (searchType === SearchCriteriaType.PATIENT_NAME) {
      params.patientName = encodeURIComponent(this.patientName);
      this.buildDateRangeParam(params, 'timeOfExam', this.timeOfExam, isRequestParams);
    } else if (searchType === SearchCriteriaType.MRN) {
      params.mrn = encodeURIComponent(this.buildServiceParamsWithWildcard(this.mrn));
    } else if (searchType === SearchCriteriaType.NHI) {
      params.nhi = encodeURIComponent(this.buildServiceParamsWithWildcard(this.nhi));
    } else {
      const keys = Object.keys(this);
      keys.forEach((key: string) => {
        if (this.skipProps && this.skipProps.findIndex(prop => prop === key) < 0) {
          const value = this[key];
          if (value !== undefined) {
            if (typeof value === 'string') {
              if (!isEmpty(value)) {
                params[key] = this.buildTextParam(key, value);
              }
            } else if (value.hasOwnProperty('startTime')) {
              this.buildDateRangeParam(params, key, value, isRequestParams);
            } else if (value instanceof Array && value.length) {
              const arrValues = this.buildDropdownParam(value, SearchParamKeys[key]);
              if (arrValues && arrValues.length) {
                params[key] = arrValues;
              }
            } else if (typeof value === 'boolean') {
              params[key] = value;
            } else {
              log.warn('getFormattedSearchParams - skipped property - ', key, value, typeof value);
            }
          }
        }
      });
    }
    // Special case for department ckey
    if (!isEmpty(params.departmentId)) {
      params.departmentIds = params.departmentIds || [];
      params.departmentIds.push(params.departmentId);
      delete params.departmentId;
    }
    return params;
  }

  private buildDateRangeParam(params: any, key: string, value: DateRange, isRequestParam: boolean) {
    if (key === 'timeOfExam') {
      if (value.option === DatepickerRangeOptions.customRange) {
        this.getDateParams(params, value, DateRangeKeys.timeOfExamBeginDate, DateRangeKeys.timeOfExamEndDate);
      } else if (value.option !== DatepickerRangeOptions.allDates) {
        params[isRequestParam ? DateRangeKeys.timeOfExamToken : 'timeOfExamDateToken'] = encodeURIComponent(
          appConstants.datePickerRangeValues[value.option]
        );
      }
    } else if (key === 'verifiedDateTime') {
      if (value.option === DatepickerRangeOptions.customRange) {
        return this.getDateParams(
          params,
          value,
          DateRangeKeys.verifiedExamBeginDate,
          DateRangeKeys.verifiedExamEndDate
        );
      } else if (value.option !== DatepickerRangeOptions.allDates) {
        params[isRequestParam ? DateRangeKeys.verifiedExamToken : 'verifiedTimeOfExamDateToken'] = encodeURIComponent(
          appConstants.datePickerRangeValues[value.option]
        );
      }
    } else if (key === 'dob') {
      return this.getDateParams(params, value, DateRangeKeys.patientBirthBeginDate, DateRangeKeys.patientBirthEndDate);
    }
  }

  private getDateParams(params: any, value: DateRange, beginDate: string, endDate: string) {
    if (value.startTime) {
      params[beginDate] = value.startTime.format(appConstants.ADV_SEARCH_DEFAULT_DATE_FORMAT);
    }

    if (value.endTime) {
      params[endDate] = value.endTime.format(appConstants.ADV_SEARCH_DEFAULT_DATE_FORMAT);
    }
  }

  private buildTextParam(key: string, value: string): string {
    if (key === 'mrn' || key === 'accession' || key === 'nhi') {
      return encodeURIComponent(this.buildServiceParamsWithWildcard(value));
    } else if (key === 'patientName') {
      return encodeURIComponent(value);
    } else {
      return value;
    }
  }

  private buildDropdownParam(value: any[], propertyName: string): any[] {
    const values = value.map(obj => obj[propertyName]);
    if (values && values.length) {
      return values;
    }
  }

  private buildServiceParamsWithWildcard(value: string) {
    const isPrefix = PrefixSuffixMap.prefixRegex.test(value);
    const isSuffix = PrefixSuffixMap.suffixRegex.test(value);
    if (isPrefix) {
      const lastIndex = value.length - 1;
      return PrefixSuffixMap.matchPrefix + value.trim().slice(0, lastIndex);
    }
    if (isSuffix) {
      return PrefixSuffixMap.matchSuffix + value.trim().substr(1);
    }
    return PrefixSuffixMap.matchExact + value;
  }
}

export enum SearchCriteriaType {
  ADVANCED_SEARCH = 'advancedSearch',
  ACCESSION = 'accession',
  MRN = 'mrn',
  NHI = 'nhi',
  PATIENT_NAME = 'patientName',
  CUSTOM_WL = 'customWL',
  REMOTE_SEARCH = 'SearchRemotePatient',
  STUDY_INSTANCE_UID = 'studyInstanceUID'
}

export const SearchType = {
  patientName: {}
};

export const PrefixSuffixMap = {
  prefixRegex: /[%|*]$/,
  suffixRegex: /^[%|*]/,
  matchPrefix: 'prefix:',
  matchExact: 'exact:',
  matchSuffix: 'suffix:'
};

export enum DateRangeKeys {
  timeOfExamToken = 'dateToken',
  timeOfExamBeginDate = 'beginDate',
  timeOfExamEndDate = 'endDate',
  verifiedExamToken = 'verifiedDateRangeToken',
  verifiedExamBeginDate = 'verifiedBeginDate',
  verifiedExamEndDate = 'verifiedEndDate',
  patientBirthBeginDate = 'patientBirthBeginDate',
  patientBirthEndDate = 'patientBirthEndDate'
}

export enum SearchParamKeys {
  modality = 'name',
  currentLocation = 'name',
  orderingLocation = 'id',
  initialInterpreters = 'id',
  radiologists = 'id',
  designatedAuthor = 'id',
  referringPhysician = 'id',
  instituteList = 'instituteId',
  bodyParts = 'name',
  departmentIds = 'id',
  specialties = 'name',
  examStatusIds = 'examStatusNumber',
  descriptions = 'id',
  reportStatusIds = 'reportStatusId',
  site = 'name',
  equipment = 'id',
  aiFinding = 'id',
  priority = 'id',
  exceptions = 'id'
}

export const SearchParamConversionKeys = {
  worklistId: { key: 'worklistId', type: 'number' },
  statsOnTop: { key: 'statsOnTop', type: 'boolean' },
  viewerPreferences: { key: 'viewerPreference', type: 'boolean' },
  radiologistList: { key: 'radiologists', type: 'Array' },
  designatedAuthorList: { key: 'designatedAuthor', type: 'Array' },
  patientLocationList: { key: 'currentLocation', type: 'Array' },
  initialInterpreterList: { key: 'initialInterpreters', type: 'Array' },
  examPriorityList: { key: 'priority', type: 'Array' },
  worklistName: { key: 'worklistName', type: 'string' },

  examDateRangeToken: { key: '', type: '', skip: true },
  beginExamDateRange: { key: '', type: '', skip: true },
  endExamDateRange: { key: '', type: '', skip: true },
  verifiedDateRangeToken: { key: '', type: '', skip: true },
  verifiedBeginDate: { key: '', type: '', skip: true },
  verifiedEndDate: { key: '', type: '', skip: true },
  patientBirthBeginDate: { key: '', type: '', skip: true },
  patientBirthEndDate: { key: '', type: '', skip: true },

  patientName: { key: 'patientName', type: 'string' },
  patientIdentifier: { key: '', type: '', skip: true },
  accession: { key: 'accession', type: 'string' },
  departmentCkey: { key: 'departmentId', type: 'string' },
  orderingLocationCkey: { key: 'orderingLocationId', type: 'string' },
  orderNumber: { key: 'orderNumber', type: 'string' },
  modalityList: { key: 'modality', type: 'Array' },
  examStatusList: { key: 'examStatusIds', type: 'Array' },
  filterParamsList: { key: '', type: 'Array' },
  procedureDescriptionList: { key: 'descriptions', type: 'Array' },
  orderingLocationList: { key: 'orderingLocation', type: 'Array' },
  protocolStatusList: { key: 'protocolStatus', type: 'Array' },
  bodyPartList: { key: 'bodyParts', type: 'Array' },
  departmentList: { key: 'departmentIds', type: 'Array' },
  examSpecialtyList: { key: 'specialties', type: 'Array' },
  instituteList: { key: 'instituteList', type: 'Array' },
  reportStatusList: { key: 'reportStatusIds', type: 'Array' },
  equipmentList: { key: 'equipment', type: 'Array' },
  siteList: { key: 'site', type: 'Array' },
  exceptions: { key: 'exceptions', type: 'Array' },
  orderingLocationDtoList: { key: '', type: 'Array', skip: true },
  assignedToList: { key: '', type: 'Array', skip: true },
  excludeZeroImageExams: { key: 'excludeZeroImageExams', type: 'boolean' },
  referringPhysician: { key: 'referringPhysician', type: 'Array' },
  orderBy: { key: 'orderBy', type: 'boolean' },
  //Start: params for linked work list
  isLinked: { key: '', type: 'boolean' },
  filterJson: { key: '', type: '', skip: true },
  parentWorkListId: { key: '', type: 'number' },
  aifindingList: { key: 'aiFinding', type: 'Array' }
};
